import { z } from "zod";
import { BREAKPOINTS, DashboardBodySchema, type DashboardBody } from "./dashboard";
import type { SemanticQuery, SemanticQueryInput } from "./query";
import { WidgetDefinitionSchema, type WidgetDefinition } from "./widget";

/**
 * The marketplace: anyone can publish a dashboard or a single widget, and anyone in any org can
 * install a copy. A listing holds definitions only, never data or scope: whoever previews or
 * installs one sees their own locations.
 */

export const CATEGORIES = ["sales", "menu", "payments", "labor", "operations", "other"] as const;
export type Category = (typeof CATEGORIES)[number];
export const CATEGORY_LABELS: Record<Category, string> = {
  sales: "Sales & Revenue",
  menu: "Menu & Products",
  payments: "Payments",
  labor: "Labor & Servers",
  operations: "Operations",
  other: "Other",
};

const MODEL_CATEGORY: Record<string, Category> = {
  sales: "sales", sales_lines: "sales", orders: "sales", ledger: "sales",
  products: "menu", order_items: "menu",
  payments: "payments",
  servers: "labor",
  locations: "operations",
};

/** The category most of these queries' metrics belong to (by model), or "other". */
export function suggestCategory(queries: (SemanticQuery | SemanticQueryInput)[]): Category {
  const votes = new Map<Category, number>();
  for (const q of queries) {
    for (const m of q.metrics) {
      if (m.kind !== "metric") continue;
      const c = MODEL_CATEGORY[m.ref.split(".")[0]!];
      if (c) votes.set(c, (votes.get(c) ?? 0) + 1);
    }
  }
  let best: Category = "other";
  let n = 0;
  for (const c of CATEGORIES) {
    if ((votes.get(c) ?? 0) > n) [best, n] = [c, votes.get(c)!];
  }
  return best;
}

export const LISTING_KINDS = ["dashboard", "widget"] as const;
export type ListingKind = (typeof LISTING_KINDS)[number];
export const MAX_TAGS = 8;

const TagSchema = z.string().trim().toLowerCase().min(1).max(24).regex(/^[a-z0-9][a-z0-9 &+-]*$/, "Tags are letters, numbers, spaces and - & +");

export const PublishRequestSchema = z.object({
  kind: z.enum(LISTING_KINDS),
  /** The publisher's own dashboard or widget id. */
  sourceId: z.string().min(1).max(64),
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).default(""),
  category: z.enum(CATEGORIES),
  tags: z.array(TagSchema).max(MAX_TAGS).default([]).transform((t) => [...new Set(t)]),
  /** What changed in this version. */
  notes: z.string().trim().max(1000).optional(),
  /** Put an unlisted listing back in the marketplace along with this version. */
  relist: z.boolean().optional(),
});
export type PublishRequest= z.infer<typeof PublishRequestSchema>;
export type PublishRequestInput = z.input<typeof PublishRequestSchema>;

/**
 * What an install copies, frozen at publish time. Widget ids are replaced by stable keys, and the
 * dashboard's items reference those keys, so a listing never points at anyone's live widgets.
 */
export const ListingSnapshotSchema = z.object({
  widgets: z.array(z.object({ key: z.string().min(1).max(64), definition: WidgetDefinitionSchema })).min(1).max(60),
  dashboard: DashboardBodySchema.optional(),
});
export type ListingSnapshot = z.infer<typeof ListingSnapshotSchema>;

export const LISTING_SORTS = ["popular", "new", "liked"] as const;
export type ListingSort = (typeof LISTING_SORTS)[number];

export interface Listing {
  id: string;
  kind: ListingKind;
  title: string;
  description: string;
  category: Category;
  tags: string[];
  /** The publisher's name, or "GoTab" for staff. */
  publisher: string;
  /** Present for listings the viewer published. */
  sourceId?: string;
  mine: boolean;
  version: number;
  status: "listed" | "unlisted";
  verified: boolean;
  featured: boolean;
  /** Unique people who have installed it. */
  installers: number;
  likes: number;
  likedByMe: boolean;
  /** The oldest version among the viewer's copies still on one of their dashboards, if any. */
  installedVersion?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListingDetail extends Listing {
  snapshot: ListingSnapshot;
  notes?: string;
  versions: ListingVersion[];
  /** The viewer's copies that are still on one of their dashboards. */
  copies: InstalledCopy[];
}

export interface ListingVersion { version: number; notes?: string; createdAt: string }

/** A copy the viewer owns: a dashboard, or a widget on one of their dashboards. */
export interface InstalledCopy {
  /** The copied dashboard, or the dashboard the copied widget is on. */
  dashboardId: string;
  dashboardTitle: string;
  /** Set for widget listings. */
  widgetId?: string;
  version: number;
}

/**
 * Update a copy to the listing's current version, in place: it keeps its id, shares, title and
 * filters. `revision` is the dashboard revision (or, for a widget, the widget version) the preview
 * was based on, so edits made after it aren't overwritten unseen.
 */
export const UpdateCopyRequestSchema = z.union([
  z.object({ dashboardId: z.string().min(1).max(64), revision: z.number().int() }),
  z.object({ widgetId: z.string().min(1).max(64), revision: z.number().int() }),
]);
export type UpdateCopyRequest = z.infer<typeof UpdateCopyRequestSchema>;
export type UpdateTarget = { dashboardId: string } | { widgetId: string };

/** What updating a copy would do. */
export interface UpdatePreview {
  from: number;
  to: number;
  /** The versions after `from`, newest first. */
  versions: ListingVersion[];
  /**
   * What the copy changed since it was installed, which the update replaces. Null when the
   * installed version can't be compared (so any changes are replaced).
   */
  replaces: { edited: string[]; removed: string[]; layout: boolean } | null;
  /** Widgets the user added to the dashboard themselves; the update keeps them. */
  kept: string[];
  /** Send back with the update. */
  revision: number;
}

/** Where an installed widget goes: onto one of your dashboards, or a new one. Dashboards always install as new. */
export const InstallRequestSchema = z.object({
  target: z.union([
    z.object({ newDashboard: z.string().trim().min(1).max(120) }),
    z.object({ dashboardId: z.string().min(1).max(64) }),
  ]),
});
export type InstallRequest = z.infer<typeof InstallRequestSchema>;

/** A copied dashboard as an update sees it: its body, and its widgets' definitions, marked when they came from the listing. */
export interface CopyState {
  body: DashboardBody;
  widgets: Record<string, { definition: WidgetDefinition; fromListing: boolean }>;
}

/** JSON with object keys sorted, so equal definitions compare equal whatever order they were built in. */
const stable = (v: unknown): string =>
  Array.isArray(v)
    ? `[${v.map(stable).join(",")}]`
    : v && typeof v === "object"
      ? `{${Object.keys(v).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined)
        .map((k) => `${JSON.stringify(k)}:${stable((v as Record<string, unknown>)[k])}`).join(",")}}`
      : JSON.stringify(v);
export const sameDefinition = (a: WidgetDefinition, b: WidgetDefinition) => stable(a) === stable(b);

/**
 * The copy's items that belong to the listing: ones installed from it that sit where the installed
 * version put them (layout key `i`). Everything else on the dashboard is the user's own.
 */
function splitItems(copy: CopyState, base: ListingSnapshot | null) {
  const baseIs = new Set(base?.dashboard?.items.map((it) => it.i));
  const listing: DashboardBody["items"] = [];
  const own: DashboardBody["items"] = [];
  for (const it of copy.body.items) {
    const w = copy.widgets[it.widgetId];
    if (!w) continue;
    (w.fromListing && (!base || baseIs.has(it.i)) ? listing : own).push(it);
  }
  return { listing, own };
}

/**
 * What a copied dashboard changed since the installed version `base`, which an update replaces,
 * and the widgets the user added, which it keeps. `replaces` is null without a base to compare to.
 */
export function diffCopy(base: ListingSnapshot | null, copy: CopyState): Pick<UpdatePreview, "replaces" | "kept"> {
  const { listing, own } = splitItems(copy, base);
  const kept = [...new Set(own.map((it) => copy.widgets[it.widgetId]!.definition.title))];
  if (!base?.dashboard) return { replaces: null, kept };
  const defs = new Map(base.widgets.map((w) => [w.key, w.definition]));
  const byI = new Map(listing.map((it) => [it.i, it]));
  const edited = new Set<string>();
  const removed = new Set<string>();
  for (const it of base.dashboard.items) {
    const def = defs.get(it.widgetId)!;
    const mine = byI.get(it.i);
    if (!mine) removed.add(def.title);
    // Named as the user sees it now, which may be their own rename.
    else if (!sameDefinition(copy.widgets[mine.widgetId]!.definition, def)) edited.add(copy.widgets[mine.widgetId]!.definition.title);
  }
  // Only compare positions both sides have: the grid fills in breakpoints a publisher never arranged.
  const layout = BREAKPOINTS.some((bp) => {
    const at = new Map(copy.body.layout[bp].map((l) => [l.i, l]));
    return base.dashboard!.layout[bp].some((l) => {
      const c = byI.has(l.i) ? at.get(l.i) : undefined;
      return c && (c.x !== l.x || c.y !== l.y || c.w !== l.w || c.h !== l.h);
    });
  });
  return { replaces: { edited: [...edited], removed: [...removed], layout }, kept };
}

/**
 * The copy's dashboard at version `next`: the listing's widgets and layout become `next`'s, and
 * the user's own widgets are kept below them. The title and filters stay the user's. Widgets are
 * reused where `next` has one at the same layout key, so their ids and history survive; the rest
 * are new (`newId`). Returns the body and each widget to write, by snapshot key.
 */
export function mergeUpdate(base: ListingSnapshot | null, next: ListingSnapshot, copy: CopyState, newId: () => string) {
  const { listing, own } = splitItems(copy, base);
  const at = new Map(listing.map((it) => [it.i, it.widgetId]));
  const ids = new Map<string, string>();
  const used = new Set<string>();
  for (const it of next.dashboard!.items) {
    const id = at.get(it.i);
    if (id && !ids.has(it.widgetId) && !used.has(id)) ids.set(it.widgetId, id), used.add(id);
  }
  const widgets = next.widgets.map((w) => {
    const id = ids.get(w.key);
    return id ? { key: w.key, id, reused: true } : { key: w.key, id: newId(), reused: false };
  });
  const idOf = new Map(widgets.map((w) => [w.key, w.id]));

  // Keep the user's widgets, under a fresh layout key if `next` took theirs.
  const taken = new Set(next.dashboard!.items.map((it) => it.i));
  const rename = new Map(own.map((it) => [it.i, taken.has(it.i) ? newId().slice(0, 8) : it.i]));
  const layout = Object.fromEntries(BREAKPOINTS.map((bp) => {
    const theirs = next.dashboard!.layout[bp];
    const bottom = Math.max(0, ...theirs.map((l) => l.y + l.h));
    const mine = copy.body.layout[bp].filter((l) => rename.has(l.i));
    const top = Math.min(...mine.map((l) => l.y));
    return [bp, [...theirs, ...mine.map((l) => ({ ...l, i: rename.get(l.i)!, y: bottom + l.y - top }))]];
  })) as DashboardBody["layout"];
  const body: DashboardBody = {
    ...copy.body,
    items: [
      ...next.dashboard!.items.map((it) => ({ i: it.i, widgetId: idOf.get(it.widgetId)! })),
      ...own.map((it) => ({ ...it, i: rename.get(it.i)! })),
    ],
    layout,
  };
  return { body, widgets };
}
